import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const SecuritySection = () => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const [loginActivity] = useState([
    {
      id: 1,
      device: 'Chrome on Windows',
      location: 'Bangalore, Karnataka',
      ipAddress: '103.xxx.xxx.xxx',
      date: '27/11/2025 05:15 AM',
      isCurrent: true
    },
    {
      id: 2,
      device: 'Safari on iPhone',
      location: 'Mumbai, Maharashtra',
      ipAddress: '103.xxx.xxx.xxx',
      date: '25/11/2025 02:30 PM',
      isCurrent: false
    },
    {
      id: 3,
      device: 'Chrome on Android',
      location: 'Bangalore, Karnataka',
      ipAddress: '103.xxx.xxx.xxx',
      date: '23/11/2025 09:45 AM',
      isCurrent: false
    }
  ]);

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!passwordData?.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!passwordData?.newPassword) newErrors.newPassword = 'New password is required';
    if (passwordData?.newPassword?.length < 8) newErrors.newPassword = 'Password must be at least 8 characters';
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSavePassword = () => {
    if (validatePassword()) {
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({});
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Lock" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Password</h2>
              <p className="text-sm text-muted-foreground">Change your password to keep your account secure</p>
            </div>
          </div>
          {!isChangingPassword && (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit2"
              iconPosition="left"
              onClick={() => setIsChangingPassword(true)}
            >
              Change
            </Button>
          )}
        </div>

        {isChangingPassword && (
          <div className="space-y-4">
            <Input
              label="Current Password"
              name="currentPassword"
              type="password"
              value={passwordData?.currentPassword}
              onChange={handlePasswordChange}
              error={errors?.currentPassword}
              required
              placeholder="Enter current password"
            />

            <Input
              label="New Password"
              name="newPassword"
              type="password"
              value={passwordData?.newPassword}
              onChange={handlePasswordChange}
              error={errors?.newPassword}
              required
              placeholder="Enter new password"
              description="Must be at least 8 characters"
            />

            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={passwordData?.confirmPassword}
              onChange={handlePasswordChange}
              error={errors?.confirmPassword}
              required
              placeholder="Re-enter new password"
            />

            <div className="flex flex-wrap gap-3 pt-4">
              <Button
                variant="default"
                iconName="Check"
                iconPosition="left"
                onClick={handleSavePassword}
              >
                Update Password
              </Button>
              <Button
                variant="outline"
                iconName="X"
                iconPosition="left"
                onClick={handleCancelPassword}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="ShieldCheck" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h2>
            <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="Enable two-factor authentication"
            description="Require a verification code in addition to your password when signing in"
            checked={twoFactorEnabled}
            onChange={() => setTwoFactorEnabled(!twoFactorEnabled)}
          />

          {twoFactorEnabled && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Setup Required</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    You'll need to download an authenticator app like Google Authenticator or Authy to complete the setup.
                  </p>
                  <Button variant="default" size="sm" iconName="Smartphone" iconPosition="left">
                    Setup Authenticator
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Activity" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Login Activity</h2>
            <p className="text-sm text-muted-foreground">Recent devices and locations where you've signed in</p>
          </div>
        </div>

        <div className="space-y-3">
          {loginActivity?.map((activity) => (
            <div key={activity?.id} className="flex items-start justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Monitor" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground">{activity?.device}</p>
                    {activity?.isCurrent && (
                      <span className="px-2 py-0.5 bg-success/10 text-success text-xs font-medium rounded">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{activity?.location}</p>
                  <p className="text-xs text-muted-foreground">{activity?.ipAddress} • {activity?.date}</p>
                </div>
              </div>
              {!activity?.isCurrent && (
                <button className="p-2 hover:bg-background rounded-lg transition-smooth flex-shrink-0">
                  <Icon name="X" size={16} className="text-error" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <Button variant="outline" size="sm" iconName="LogOut" iconPosition="left">
            Sign Out All Devices
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySection;